/**
 * Phase 2B — Autosave hook with the 5 founder refinements:
 *   R1) Persistent status indicator + last-saved timestamp + Save History.
 *   R2) Recovery Mode — restore unfinished session from localStorage.
 *   R3) Unsaved Changes Protection via beforeunload + react-router blocker.
 *   R4) Save states (idle/saving/saved/failed) with retry path.
 *   R5) Editing Session Timer — exposes `sessionStartedAt` for analytics.
 *
 * UAT Finding #1 hardening (Founder UAT, Library V2 Phase 1):
 *   F1) meta_version is tracked in a ref and adopted from the server once the
 *       questionnaire loads — the hook mounts before data arrives, so the old
 *       state-only tracking sent expected_meta_version=0 on every reopened
 *       session and produced deterministic single-user 409 conflicts.
 *   F2) flush() drains ALL pending edits: saves are serialized, edits queued
 *       during an in-flight request are saved by a follow-up pass instead of
 *       being silently dropped, and flush resolves `true` only when the
 *       backend has acknowledged everything (used by "Guardar y salir").
 *   F3) The localStorage recovery blob is only cleared when nothing remains
 *       pending; `hasPendingChanges()` inspects the real pending buffer.
 *
 * Single source of truth for autosave state. The consumer component
 * (AdminStudioEditor.jsx) wires the returned actions into the editor frame.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '@/lib/adminApi';

const DEBOUNCE_MS = 1500;
const STORAGE_PREFIX = 'cth:studio:recovery:';

/* -------------------------------------------------------------------------- */
/*  STATES                                                                     */
/* -------------------------------------------------------------------------- */
//   idle    — no pending changes
//   dirty   — user has typed/edited but debounce not yet fired
//   saving  — request in flight
//   saved   — last save succeeded; timestamp displayed
//   failed  — last save errored; retry path available
//   conflict — server returned 409 stale_meta_version
/* -------------------------------------------------------------------------- */

export function useAutosave({ assetId, initialMetaVersion, onSaved, onConflict }) {
  const [state, setState] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [metaVersion, setMetaVersion] = useState(initialMetaVersion || 0);
  const [sessionStartedAt] = useState(() => Date.now());

  // Mutable refs avoid stale closures inside the debounce timer.
  const pendingRef = useRef({ meta_patch: null, version_patch: null });
  const timerRef = useRef(null);
  const clientIdRef = useRef(`tab-${Math.random().toString(36).slice(2, 10)}`);
  const inFlightRef = useRef(null); // Promise<boolean> | null — active drain
  const metaVersionRef = useRef(initialMetaVersion || 0);
  const callbacksRef = useRef({ onSaved, onConflict });
  callbacksRef.current = { onSaved, onConflict };

  // F1 — adopt the server meta_version once the questionnaire loads. Only
  // move forward: our own saves may already have advanced past the prop.
  useEffect(() => {
    const mv = initialMetaVersion || 0;
    if (mv > metaVersionRef.current) {
      metaVersionRef.current = mv;
      setMetaVersion(mv);
    }
  }, [initialMetaVersion]);

  const hasPendingRaw = () =>
    !!(pendingRef.current.meta_patch || pendingRef.current.version_patch);

  // ---------- Recovery Mode (R2) ----------
  useEffect(() => {
    if (!assetId) return undefined;
    const key = STORAGE_PREFIX + assetId;
    try {
      const blob = localStorage.getItem(key);
      if (blob) {
        const parsed = JSON.parse(blob);
        // Surface via a window-level event so the editor can show a dialog.
        window.dispatchEvent(new CustomEvent('studio:recovery-available', {
          detail: { assetId, ...parsed, key },
        }));
      }
    } catch { /* ignore */ }
    return undefined;
  }, [assetId]);

  // ---------- Persist pending edits to localStorage (R2 backing store) -----
  const persistRecovery = useCallback(() => {
    if (!assetId) return;
    const key = STORAGE_PREFIX + assetId;
    const pending = pendingRef.current;
    if (!pending.meta_patch && !pending.version_patch) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({
        pending,
        meta_version: metaVersionRef.current,
        client_id: clientIdRef.current,
        last_dirty_at: new Date().toISOString(),
      }));
    } catch { /* quota or disabled */ }
  }, [assetId]);

  // ---------- Single save pass ----------
  // Returns 'saved' | 'empty' | 'failed' | 'conflict'.
  const saveOnce = useCallback(async () => {
    if (!hasPendingRaw()) return 'empty';
    setState('saving');
    const snapshot = { ...pendingRef.current };
    // Reset pending immediately so subsequent edits buffer separately.
    pendingRef.current = { meta_patch: null, version_patch: null };
    try {
      const res = await adminApi.studioAutosave(assetId, {
        client_id: clientIdRef.current,
        client_ts: new Date().toISOString(),
        expected_meta_version: metaVersionRef.current,
        meta_patch: snapshot.meta_patch || undefined,
        version_patch: snapshot.version_patch || undefined,
        auto: true,
      });
      metaVersionRef.current = res.meta_version;
      setMetaVersion(res.meta_version);
      setLastSavedAt(res.saved_at);
      setLastError(null);
      if (callbacksRef.current.onSaved) {
        // hasPending lets the consumer skip clobbering newer local edits.
        callbacksRef.current.onSaved(res, { hasPending: hasPendingRaw() });
      }
      return 'saved';
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Put the snapshot back so retry can re-send it. Merge with anything
      // typed since (the new pending wins per-key).
      pendingRef.current = {
        meta_patch: { ...(snapshot.meta_patch || {}), ...(pendingRef.current.meta_patch || {}) },
        version_patch: pendingRef.current.version_patch || snapshot.version_patch || null,
      };
      if (pendingRef.current.meta_patch && Object.keys(pendingRef.current.meta_patch).length === 0) {
        pendingRef.current.meta_patch = null;
      }
      persistRecovery();
      if (detail?.code === 'stale_meta_version') {
        setState('conflict');
        setLastError({ code: 'stale_meta_version', detail });
        if (callbacksRef.current.onConflict) callbacksRef.current.onConflict(detail);
        return 'conflict';
      }
      setState('failed');
      setLastError({ code: 'save_failed', detail: detail || err?.message || 'Unknown error' });
      return 'failed';
    }
  }, [assetId, persistRecovery]);

  // ---------- Save executor (F2 — drains everything, never drops) ----------
  const flush = useCallback(() => {
    // Serialize: a concurrent flush awaits the active drain instead of
    // dropping its pending edits.
    if (inFlightRef.current) return inFlightRef.current;
    const drain = (async () => {
      let outcome = 'empty';
      for (let pass = 0; pass < 20; pass += 1) {
        outcome = await saveOnce();
        if (outcome === 'saved' && hasPendingRaw()) continue; // edits arrived mid-flight
        break;
      }
      const clean = (outcome === 'saved' || outcome === 'empty') && !hasPendingRaw();
      if (clean) {
        if (outcome === 'saved') setState('saved');
        // F3 — clear recovery only when truly nothing is pending.
        try { localStorage.removeItem(STORAGE_PREFIX + assetId); } catch { /* ignore */ }
      }
      return clean;
    })();
    inFlightRef.current = drain.finally(() => { inFlightRef.current = null; });
    return inFlightRef.current;
  }, [assetId, saveOnce]);

  // ---------- Public mutator ----------
  const queuePatch = useCallback(({ meta = null, version = null }) => {
    if (!assetId) return;
    const cur = pendingRef.current;
    if (meta) {
      cur.meta_patch = { ...(cur.meta_patch || {}), ...meta };
    }
    if (version) {
      // Latest wins for version_patch (always whole sections+questions).
      cur.version_patch = version;
    }
    setState('dirty');
    setLastError(null);
    persistRecovery();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(), DEBOUNCE_MS);
  }, [assetId, flush, persistRecovery]);

  // ---------- Retry (R4) ----------
  const retry = useCallback(() => {
    setState('dirty');
    setLastError(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    return flush();
  }, [flush]);

  // ---------- Force save (Cmd+S) ----------
  const forceSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    return flush();
  }, [flush]);

  // ---------- Unsaved Changes Protection (R3) ----------
  useEffect(() => {
    const handler = (e) => {
      const busy = hasPendingRaw() || !!inFlightRef.current
        || state === 'dirty' || state === 'saving' || state === 'failed' || state === 'conflict';
      if (busy) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state]);

  // ---------- Cleanup ----------
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return {
    state,                  // 'idle' | 'dirty' | 'saving' | 'saved' | 'failed' | 'conflict'
    lastSavedAt,
    lastError,
    metaVersion,
    sessionStartedAt,
    queuePatch,
    flush,
    retry,
    forceSave,
    hasPendingChanges: () => hasPendingRaw() || !!inFlightRef.current
      || state === 'dirty' || state === 'saving' || state === 'failed' || state === 'conflict',
    clientId: clientIdRef.current,
  };
}

// ---------- Recovery primitives -------------------------------------------
export function readRecovery(assetId) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + assetId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearRecovery(assetId) {
  try { localStorage.removeItem(STORAGE_PREFIX + assetId); } catch { /* ignore */ }
}
